import { Component, Input, OnInit, OnChanges, ViewChild, ViewChildren, QueryList, input, output, effect, computed } from '@angular/core';
import 'chartjs-adapter-moment';
import { CrosshairPlugin } from '../plugins/chartjs-plugin-crosshair.esm';
import { interpolate } from '../plugins/interpolate';
import { RangeSelectorPlugin } from '../plugins/chartjs-plugin-range-selector';
import { ThresholdBandsPlugin, ThresholdBandConfig } from '../plugins/chartjs-plugin-threshold-bands';
import { TranslateService } from '@ngx-translate/core';
import { BaseChartDirective } from 'ng2-charts';

import { Chart, ChartType, registerables, Interaction, InteractionModeFunction } from 'chart.js';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { MenuItem } from 'primeng/api';
import { ChartOptionsButtonComponent } from '../chart-options/chart-options-button.component';
import { ChartOptionsConfig } from '../chart-options/chart-options-storage.service';
import { applyYAxisMode } from '../chart-options/apply-y-axis-mode';
import { OverlayConfig, buildOverlayDatasets, buildOverlayScale, buildPrimaryAxisTitle } from '../chart-options/overlay-axis';
import { TooltipModule } from 'primeng/tooltip';
import { ChartLegendComponent } from '../chart-legend/chart-legend.component';
import { HtmlLegendItem, HtmlLegendPlugin } from '../chart-legend/chartjs-plugin-html-legend';
import { composeChartWithLegend } from '../chart-export/chart-export';

Chart.register(...registerables); 


declare module 'chart.js' {
  interface InteractionModeMap {
    interpolate: InteractionModeFunction;
  }
}

Interaction.modes.interpolate = interpolate;

export interface DatapointClickEvent {
  /** The source data object the clicked series was built from — dataObjects[datasetIndex]. */
  sourceObject: any;
  /** Index of the clicked point within that series. */
  index: number;
  /** The variable this chart renders (this.dataset). */
  variable: string;
  x: unknown;
  y: unknown;
}

@Component({
  selector: 'cosmos-multi-line-chart',
  imports: [BaseChartDirective,
    MenuModule, ButtonModule, ChartOptionsButtonComponent, TooltipModule, ChartLegendComponent,
  ],
  templateUrl: './multi-line-chart.component.html',
  styles: [`
    :host {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
      min-width: 0;
    }

    .chart-wrapper {
      flex-grow: 1;
      position: relative;
      min-height: 400px;
      width: 100%;
      min-width: 0;
    }

    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }

    .navigator-wrapper {
      height: 60px;
      width: 100%;
      position: relative;
      margin-top: 4px;
      flex-shrink: 0;
    }
    `]
})
export class MultiLineChartComponent implements OnInit, OnChanges {

  @ViewChild(BaseChartDirective) chart: BaseChartDirective;
  @ViewChildren(BaseChartDirective) allCharts: QueryList<BaseChartDirective>;

  @Input()
  dataObjects: any[];

  @Input()
  dataset: string;

  @Input()
  params = {};

  @Input()
  reorder = false;

  @Input()
  sameOriginAllowed = false;

  @Input()
  downloadAllowed = false;

  /** Enable the navigator range selector below the chart */
  rangeSelector = input<boolean>(false);

  /** Externally-controlled range for synchronization */
  selectedRange = input<{ min: number; max: number } | null>(null);

  /** Emits when user changes the range via the navigator */
  rangeChange = output<{ min: number; max: number } | null>();

  /** Emits when the user clicks a data point (nearest point to the cursor). */
  datapointClick = output<DatapointClickEvent>();

  /** Optional coloured background bands keyed to Y-axis value ranges (guideline
   *  zones, e.g. green/amber/red). When set, the axis grows to show all bands
   *  unless the config opts out via `extendAxis: false`. */
  thresholdBands = input<ThresholdBandConfig | null>(null);

  /** Optional second variable drawn dashed on a right-hand axis (compare tool). */
  overlay = input<OverlayConfig | null>(null);
  /** Variables the user can pick as overlay; the menu button hides when empty. */
  overlayOptions = input<{ key: string; label: string }[]>([]);
  /** Currently active overlay variable key (for the menu's check mark). */
  overlayKey = input<string | null>(null);
  /** Right-axis range chosen in the options popover. In memory only — reset whenever the overlay variable changes. */
  private _y2Config: ChartOptionsConfig = { yAxisMode: 'auto-data' };
  private _lastOverlayKey: string | null = null;

  /** Headings for the options popover's two axis sections. */
  get primaryAxisText(): string | null { const o = this.overlay(); return o ? (buildPrimaryAxisTitle(o)?.text ?? null) : null; }
  get overlayAxisText(): string | null { const o = this.overlay(); return o ? buildOverlayScale(o)['title'].text : null; }

  /** Emits the picked variable key, or null to remove the overlay. */
  overlayChange = output<string | null>();

  overlayMenuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = this.overlayOptions().map((o) => ({
      label: o.label,
      icon: o.key === this.overlayKey() ? 'fa-solid fa-check' : undefined,
      command: () => this.overlayChange.emit(o.key),
    }));
    if (this.overlayKey()) {
      items.push({ separator: true });
      items.push({
        label: this.translateService.instant('measurement.chart.overlay-remove'),
        icon: 'fa-solid fa-xmark',
        command: () => this.overlayChange.emit(null),
      });
    }
    return items;
  });
  get overlayTooltip(): string { return this.translateService.instant('measurement.chart.overlay'); }

  /** Stable id for the per-chart options button/localStorage persistence
   *  (e.g. "shm_icorr"). Omit to not render the options button at all. */
  chartId = input<string | undefined>(undefined);

  sameOrigin = false;

  downloadMenuItems: MenuItem[] | undefined;

  //CHART
  chartType: ChartType = 'line';
  // ThresholdBandsPlugin no-ops unless chartOptions.plugins.thresholdBands is set.
  // HtmlLegendPlugin no-ops unless chartOptions.plugins.htmlLegend is set.
  chartPlugins = [CrosshairPlugin, ThresholdBandsPlugin, HtmlLegendPlugin];
  chartData = {
    datasets: [],
    labels: []
  };

  chartOptions: any;

  /** Current legend items — HtmlLegendPlugin (afterUpdate) refreshes this
   *  whenever chart data/visibility changes; ChartLegendComponent renders it. */
  legendItems: HtmlLegendItem[] = [];

  // NAVIGATOR
  navigatorChartData = {
    datasets: [],
    labels: []
  };
  navigatorChartOptions: any = {};
  navigatorPlugins = [RangeSelectorPlugin];

  /** Flag to suppress emitting rangeChange when the change comes from an external input */
  private _suppressRangeEmit = false;

  /** Track the currently active range so initData() can re-apply it */
  private _currentRange: { min: number; max: number } | null = null;

  /** Current Y-axis display preference from ChartOptionsButtonComponent.
   *  Defaults to auto-data (Chart.js auto-scales / suggestedMin/Max +
   *  optional band-extension only apply when explicitly chosen) until the
   *  button reports a resolved (possibly stored) config on init. */
  private _yAxisConfig: ChartOptionsConfig = { yAxisMode: 'auto-data' };

  /** Maps chart datasetIndex → index into `dataObjects` (series can be skipped
   *  when a dataObject has no array for `dataset`). Rebuilt each initData(). */
  private _datasetSourceIndex: number[] = [];

  /** Persisted "reverse dataset order" toggle from reorderDatasets(). Applied
   *  inside initData() itself (not as a one-off post-hoc array reverse) so it
   *  survives any later initData() rebuild (Y-axis change, new data, etc.)
   *  instead of silently reverting to source order, and so
   *  _datasetSourceIndex stays in sync with what's actually rendered. */
  private _reversed = false;

  constructor(
    private translateService: TranslateService,
  ) {
    this.downloadMenuItems = [
      { 
        label: this.translateService.instant('measurement.chart.download-png'),
        icon: 'fa-solid fa-file-image',
        command: () => {
            this.downloadChart();
        } 
      },
      { 
        label: this.translateService.instant('measurement.chart.download-csv'),
        icon: 'fa-solid fa-file-csv',
        command: () => {
            this.downloadCSV();
        } 
      }
    ]

    // React to external selectedRange changes (for sync)
    effect(() => {
      const range = this.selectedRange();
      if (range && this.chart?.chart) {
        this._suppressRangeEmit = true;
        this._applyRange(range.min, range.max);
        this._suppressRangeEmit = false;
      } else if (range === null && this.chart?.chart) {
        this._suppressRangeEmit = true;
        this._resetMainChartRange();
        this._suppressRangeEmit = false;
      }
    });
  }

  ngOnInit(): void {}

  ngOnChanges(changes): void {
    this.initData();
  }

  initData() {
    // Nearest-point click → emit datapointClick. `chart` is passed via the 3rd
    // callback arg (ng2-charts / chart.js). Resolves datasetIndex back to the
    // original dataObjects entry via _datasetSourceIndex (series can be skipped).
    const onClick = (event: any, _elements: any[], chart: any) => {
      if (!chart) return;
      const points = chart.getElementsAtEventForMode(event, 'nearest', { intersect: false }, true);
      if (!points?.length) return;
      const { datasetIndex, index } = points[0];
      const sourceIndex = this._datasetSourceIndex[datasetIndex];
      const sourceObject = sourceIndex != null ? this.dataObjects?.[sourceIndex] : undefined;
      if (!sourceObject) return;
      const parsed = chart.data?.datasets?.[datasetIndex]?.data?.[index] ?? {};
      this.datapointClick.emit({
        sourceObject,
        index,
        variable: this.dataset,
        x: parsed.x,
        y: parsed.y,
      });
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      onClick,
      scales: {
        // Define the X axis and its components.
        x: {
          type: 'time',
          ticks: {
            major: {
              enabled: true,
            },
          },
        },
        y: {
          type: 'linear',
          ticks: {
          }
        }
      },
      plugins: {
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false
        },
        crosshair: {
          zoom: {
            enabled: !this.rangeSelector(),
          }
        },
        legend: { display: false },
        htmlLegend: { onLabelsChange: (items: HtmlLegendItem[]) => { this.legendItems = items; } },
      }
    };

    const cleanDataObjects: {label: string, x: any[], y: any[], sourceIndex: number}[] = [];
    let datasets: any[] = [];

    this.dataObjects.forEach((dataObject, sourceIndex) => {
      if(Array.isArray(dataObject[this.dataset])) {
        const time = [];
        dataObject.time.forEach(element => {
          time.push(new Date(element));
        });
        // `label` (compare charts) is the verbatim legend text; otherwise "<name> - <variable legend>".
        const label = dataObject.label ?? (dataObject.name? (dataObject.name + ' - ' + this.translateService.instant('chart.legend.' + this.dataset)):this.translateService.instant('chart.legend.' + this.dataset));

        cleanDataObjects.push({
          label: label,
          x: time,
          y: dataObject[this.dataset],
          sourceIndex,
        });
      }
    });

    if (this._reversed) {
      cleanDataObjects.reverse();
    }

    // datasetIndex → dataObjects index, for the onClick handler above.
    this._datasetSourceIndex = cleanDataObjects.map(c => c.sourceIndex);

    if (cleanDataObjects.length) {
      cleanDataObjects.forEach((element) => {
        datasets.push(
          this.generateDataset(element.x, element.y, element.label)
        );
      });
    }

    // Overlay (second variable, right axis). Added before the sameOrigin transform so it
    // is re-based to relative time together with the primary datasets. `_datasetSourceIndex`
    // only covers primary datasets, so clicking an overlay point resolves no source (ignored).
    const overlay = this.overlay();
    if (this.overlayKey() !== this._lastOverlayKey) {
      this._lastOverlayKey = this.overlayKey();
      this._y2Config = { yAxisMode: 'auto-data' };
    }
    if (overlay) {
      datasets.push(...buildOverlayDatasets(overlay, (x, y, label) => this.generateDataset(x, y, label)));
    }

    if (this.sameOrigin) {
      // MAX TIME CALCULATION
      let dateDivider = 1000*60*60;
      let axisText = 'Horas desde el inicio - Relativas'
      datasets.forEach(dataset => {
        if(dataset.data.length) {
          const monitoringLength = (dataset.data[dataset.data.length-1].x.getTime() - dataset.data[0].x.getTime())/(1000*60*60*24);
          if (monitoringLength >= 1) {
            dateDivider = 1000*60*60*24;
            axisText = 'Días desde el inicio - Relativos'
          }
        }
      });

      datasets = datasets.map(dataset => {
        if (!dataset.data.length) return dataset;
        const zeroTime = dataset.data[0].x;
        return {
          ...dataset,
          data: dataset.data.map((dp: any) => ({
            ...dp,
            x: (new Date(dp.x).getTime() - new Date(zeroTime).getTime()) / dateDivider,
          })),
        };
      });

      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        onClick,
        scales: {
          x: {
            type: 'linear',
            title: { display: true, text: axisText },
            ticks: { major: { enabled: true } },
          },
          y: { type: 'linear', ticks: {} }
        },
        plugins: {
          tooltip: { enabled: true, mode: 'index', intersect: false },
          crosshair: { zoom: { enabled: !this.rangeSelector() } },
          legend: { display: false },
          htmlLegend: { onLabelsChange: (items: HtmlLegendItem[]) => { this.legendItems = items; } },
        }
      };
    }

    if (overlay) {
      this.chartOptions.scales.y2 = buildOverlayScale(overlay);
      applyYAxisMode(this.chartOptions.scales.y2, this._y2Config, null);
      const primaryTitle = buildPrimaryAxisTitle(overlay);
      if (primaryTitle) this.chartOptions.scales.y.title = primaryTitle;
    }

    // Threshold background bands always render (they're just background
    // fills) — applies to both the default and sameOrigin option sets.
    const bandConfig = this.thresholdBands();
    if (bandConfig?.bands?.length) {
      this.chartOptions.plugins = this.chartOptions.plugins ?? {};
      this.chartOptions.plugins.thresholdBands = bandConfig;
    }

    // Y-axis mode from ChartOptionsButtonComponent (default: auto-data, i.e.
    // params.suggestedMin/Max applied below and nothing more). 'auto-bands'
    // and 'manual' are both explicit user choices — bands are drawn either
    // way, this only controls whether the axis is grown/pinned to them.
    applyYAxisMode(this.chartOptions.scales.y, this._yAxisConfig, bandConfig, this.params['suggestedMin'], this.params['suggestedMax']);

    // Replace reference so ng2-charts detects the change under OnPush
    this.chartData = { datasets, labels: [] };

    // Setup navigator chart if rangeSelector is enabled
    if (this.rangeSelector()) {
      this._setupNavigator();
    }

    // Re-apply the current range if one is active (survives initData rebuilds)
    if (this._currentRange && this.rangeSelector()) {
      this.chartOptions.scales.x.min = this._currentRange.min;
      this.chartOptions.scales.x.max = this._currentRange.max;
    }

    if(this.chart) {
      this.chart.chart.update();
    }
  }

  /** Prepare navigator chart data and options */
  private _setupNavigator() {
    // Clone the datasets for the navigator (simplified: no interpolation, smaller points)
    this.navigatorChartData = {
      // Overlay datasets live on the secondary axis, which the navigator doesn't have — leaving
      // them in makes Chart.js auto-create a visible y2 scale. The navigator tracks the primary series only.
      datasets: this.chartData.datasets.filter(ds => !ds.overlayKey).map(ds => ({
        ...ds,
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 1,
        interpolate: false,
      })),
      labels: []
    };

    // Determine the X axis type from the main chart
    const xType = this.chartOptions.scales?.x?.type || 'time';

    this.navigatorChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      scales: {
        x: {
          type: xType,
          ticks: {
            display: false,
          },
          grid: {
            display: false,
          },
        },
        y: {
          type: 'linear',
          ticks: {
            display: false,
          },
          grid: {
            display: false,
          },
        }
      },
      plugins: {
        tooltip: { enabled: false },
        legend: { display: false },
        rangeSelector: {
          enabled: true,
          onRangeChange: (min: number, max: number) => {
            this._onNavigatorRangeChange(min, max);
          },
          onRangeReset: () => {
            this._resetMainChartRange();
            if (!this._suppressRangeEmit) {
              this.rangeChange.emit(null);
            }
          }
        }
      }
    };
  }

  /** Called by navigator plugin when user drags the range */
  private _onNavigatorRangeChange(min: number, max: number) {
    this._applyRange(min, max);

    if (!this._suppressRangeEmit) {
      this.rangeChange.emit({ min, max });
    }
  }

  /** Apply a range to the main chart X axis */
  private _applyRange(min: number, max: number) {
    // Persist range in the bound chartOptions so it survives re-renders
    this._currentRange = { min, max };
    if (this.chartOptions?.scales?.x) {
      this.chartOptions.scales.x.min = min;
      this.chartOptions.scales.x.max = max;
    }

    if (!this.chart?.chart) return;

    const mainChart = this.chart.chart;
    mainChart.options.scales['x'].min = min;
    mainChart.options.scales['x'].max = max;
    mainChart.update('none');

    // Also update the navigator visual (for sync from external)
    this._syncNavigatorVisual(min, max);
  }

  /** Reset main chart to show full range */
  private _resetMainChartRange() {
    this._currentRange = null;
    if (this.chartOptions?.scales?.x) {
      delete this.chartOptions.scales.x.min;
      delete this.chartOptions.scales.x.max;
    }

    if (!this.chart?.chart) return;

    const mainChart = this.chart.chart;
    delete mainChart.options.scales['x'].min;
    delete mainChart.options.scales['x'].max;
    mainChart.update('none');

    // Reset navigator
    const navigatorCharts = this.allCharts?.toArray();
    if (navigatorCharts && navigatorCharts.length > 1) {
      const navChart = navigatorCharts[1].chart;
      if (navChart) {
        RangeSelectorPlugin.resetRange(navChart);
      }
    }
  }

  /** Update navigator plugin selection to match a range (for external sync) */
  private _syncNavigatorVisual(min: number, max: number) {
    const navigatorCharts = this.allCharts?.toArray();
    if (navigatorCharts && navigatorCharts.length > 1) {
      const navChart = navigatorCharts[1].chart;
      if (navChart) {
        RangeSelectorPlugin.setRange(navChart, min, max);
      }
    }
  }

  /** ChartOptionsButtonComponent (configChange) handler — fires once on init
   *  (with the resolved stored-or-default config) and again on every user
   *  edit.
   *  - sameOrigin changing means a different X-axis type (time <-> linear)
   *    and re-derived dataset X values, which only initData()'s full rebuild
   *    handles (same as the old changeOriginDatasets() button did) — a live
   *    scale-option mutation isn't enough here.
   *  - On the very first call (right after this component's own first
   *    initData(), which always builds with the {yAxisMode:'auto-data'}
   *    default since ChartOptionsButtonComponent hasn't read localStorage
   *    yet at that point), @ViewChild(BaseChartDirective) isn't resolved yet
   *    either — @ViewChild only resolves after ngAfterViewInit, which runs
   *    AFTER every child's ngOnInit, including this button's. Mutating
   *    chartOptions.scales.y in place here does nothing in that case: the
   *    object reference doesn't change, so Angular's [options]="chartOptions"
   *    binding never re-syncs it into chart.js, and the chart silently keeps
   *    rendering the stale auto-data config from the first initData() call.
   *    So: no live chart yet -> re-run initData() itself (already reads the
   *    now-updated _yAxisConfig) instead of trying to live-mutate.
   *  - Once the chart exists, Y-axis-mode-only changes stay on the cheaper
   *    live-mutate path so they don't reset the X range/navigator. */
  onChartOptionsChange(config: ChartOptionsConfig): void {
    this._yAxisConfig = config;
    const sameOriginChanged = !!config.sameOrigin !== this.sameOrigin;
    this.sameOrigin = !!config.sameOrigin;

    if (sameOriginChanged || !this.chart?.chart) {
      this.initData();
      return;
    }

    if (this.chartOptions?.scales?.y) {
      applyYAxisMode(this.chartOptions.scales.y, this._yAxisConfig, this.thresholdBands(), this.params['suggestedMin'], this.params['suggestedMax']);
    }

    const mainChart = this.chart.chart;
    applyYAxisMode(mainChart.options.scales.y, this._yAxisConfig, this.thresholdBands(), this.params['suggestedMin'], this.params['suggestedMax']);
    mainChart.update();
  }

  /** ChartOptionsButtonComponent (overlayAxisChange) handler — applies the right-axis range live. */
  onOverlayAxisChange(config: ChartOptionsConfig): void {
    this._y2Config = config;
    if (this.chartOptions?.scales?.y2) applyYAxisMode(this.chartOptions.scales.y2, config, null);
    const live = this.chart?.chart?.options?.scales?.['y2'];
    if (live) {
      applyYAxisMode(live as Record<string, any>, config, null);
      this.chart.chart.update();
    }
  }

  generateDataset(dataX, dataY, label, color?, type?) {
    const data = [];

    dataX.forEach((element,index) => {
      data.push({ x: element, y: dataY[index] });
    });

    const dataset = {
      // backgroundColor: color,
      // borderColor: color,
      showLine: true,
      fill: false,
      pointRadius: (dataY.length>480)?0:2,
      label: label,
      data: data,
      lineTension: 0,
      interpolate: true,
      cubicInterpolationMode: 'monotone'
    };

    if (type == 'threshold') { dataset['borderDash'] = [5,5] }

    return dataset;
  }

  reorderDatasets() {
    // A post-hoc reverse of the already-built chartData.datasets array
    // (the old approach) doesn't reliably repaint: Chart.js's update() caches
    // per-dataset-index draw state (line paths, point positions), so swapping
    // array order without a full rebuild left the legend (which reads
    // chart.data.datasets[i].label fresh) showing the new order while the
    // plotted lines stayed put. Persisting the flag and going through
    // initData()'s normal build path — same one sameOrigin toggling already
    // uses for a reliable full redraw — fixes both that and _datasetSourceIndex
    // staying in sync with what's actually rendered.
    this._reversed = !this._reversed;
    this.initData();
  }

  downloadChart() {
    const chartElement = document.getElementById('comparechart-' + this.dataset) as HTMLCanvasElement;

    const composed = composeChartWithLegend(chartElement, this.legendItems);

    const link = document.createElement('a');
    link.download = 'chart-' + this.dataset + '-' + new Date().getTime() + '.png';
    link.href = composed.toDataURL('image/png');
    link.click();
  }

  downloadCSV() {
    const csvContent = this.generateCSV();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = `data-${this.dataset}-${new Date().getTime()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private generateCSV(): string {
    const headers = [];
    const rows = [];

    // Build headers
    this.chartData.datasets.forEach(dataset => {
      const baseName = dataset.label.split(' - ')[0];
      headers.push(`${baseName}-time`, `${baseName}-${dataset.overlayKey ?? this.dataset}`);
    });

    // Find max data points count
    const maxPoints = Math.max(...this.chartData.datasets.map(d => d.data.length));

    // Build rows
    for (let i = 0; i < maxPoints; i++) {
      const row = [];
      this.chartData.datasets.forEach(dataset => {
        const point = dataset.data[i];
        if (point) {
          const x = point.x instanceof Date ? `"${point.x.toISOString()}"` : point.x;
          row.push(x, point.y);
        } else {
          row.push('', ''); // Empty cells if dataset has fewer points
        }
      });
      rows.push(row.join(','));
    }

    return [headers.join(','), ...rows].join('\n');
  }

  /** ChartLegendComponent (toggle) handler — click a legend item to show/hide
   *  that dataset. hideDataset()/isDatasetHidden() are BaseChartDirective's
   *  own methods (they call chart.update() internally), which also re-fires
   *  HtmlLegendPlugin's afterUpdate so legendItems refreshes with the new
   *  hidden state automatically. */
  onLegendToggle(datasetIndex: number): void {
    const chart = this.chart;
    if (!chart) return;
    chart.hideDataset(datasetIndex, !chart.isDatasetHidden(datasetIndex));
  }
}
